import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { FbrItemClientModel, FbrClientModel, FbrStatusEnum, FbrItemGroupingEnum, FbrItemOutcomeStepEnum, LinkedIncidentModel } from "../../../../../models/fbrs/fbr.models";
import { ChildComponent } from "../../child-component";
import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { UserSecurityInfoModel } from "../../../../../models/authentication/token.models";
import { Assignment } from "../../../../components/assignment/assignment";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { DynamicColumnModel, EntityDynamicValue, EntityTypeConfigurationModel, EntityTypeConfigurationHelper } from "../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { DataType } from "../../../../../models/queries/queries.models";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { Reflection } from "../../../../../utilities/reflection";
import * as Enumerable from "linq";
import { FbrService } from "../../../../../services/fbr/fbr.service";
import { CfrStatusEnum } from "../../../../../models/cfr/cfr.models";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { CfrService } from "../../../../../services/cfr/cfr.service";
import { AddSubtaskRequest } from "../../../../../models/vetting/vetting.models";
import { RightshipEntityAttachmentManager } from "../../common-steps/rightship-entity-attachment/rightship-entity-attachment-manager";
import { CargoTypeCategoryEnum } from "../../../../../models/vessels/vessel.models";
import { MobileHelper } from "../../../../../helpers/mobile-helper";

@autoinject
@transient()
@useView('views/pages/modules/fbr/tabs/fbr-responses.html')
export class FbrResponses extends ChildComponent<FbrClientModel> {

    currentUserId: number;

    changeStatusToAwaitingResponse: boolean = true;
    assignToMe: any;
    isRightShipUser: boolean;
    canAssignFbrRequest: boolean;
    currentUser: UserSecurityInfoModel;
    passed: number = FbrItemOutcomeStepEnum.Pass;
    fbrResponsesContent: HTMLElement;
    assignment: Assignment;
    canEditFbrOutcome: boolean;
    canReviewFbr: boolean;
    cfrStatuses: LookupValue[] = [];
    private canAddCFRPermission: boolean;

    get isClosed() {
        return this.item && (this.item.fbrStatusId == FbrStatusEnum.Closed || this.item.fbrStatusId == FbrStatusEnum.Expired);
    }

    @computedFrom(Reflection.getPropertyPath<FbrResponses>(z => z.currentItem),
    Reflection.getPropertyPath<FbrResponses>(z => z.currentItem.cfrStatusId))
    get isCfrClosed() {
        return this.currentItem && (this.currentItem.cfrStatusId == CfrStatusEnum.Closed || this.currentItem.cfrStatusId == CfrStatusEnum.Cancelled);
    }

    get showIncludeComment() {
        return true;
    }

    @computedFrom(Reflection.getPropertyPath<FbrResponses>(z => z.currentItem),
    Reflection.getPropertyPath<FbrResponses>(z => z.currentItem.linkedIncidents),
    Reflection.getPropertyPath<FbrResponses>(z => z.currentItem.linkedIncidents.length))
    get linkedIncidentId() {
        if (this.currentItem)
            return this.currentItem.linkedIncidents.map(x => x.incidentId);
        return [];
    }

    @computedFrom(Reflection.getPropertyPath<FbrResponses>(z => z.currentItem),
    Reflection.getPropertyPath<FbrResponses>(z => z.currentItem.cfrId),
    Reflection.getPropertyPath<FbrResponses>(z => z.currentItem.outcome),
    Reflection.getPropertyPath<FbrResponses>(z => z.canAddCFRPermission))
    get canAddCfr()
    {
        return this.canAddCFRPermission && this.currentItem && !this.currentItem.cfrId && this.currentItem.outcome == FbrItemOutcomeStepEnum.Fail;
    }

    constructor(notificationsService: NotificationClientService, i18N: I18N,
        private userClientService: UserClientService,
        private router: Router,
        private fbrService: FbrService,
        private cacheClient: CacheClientService,
        private userClaimsService: UserClaimsService,
        private signalClientService: SignalClientService,
        public notificationClientService: NotificationClientService,
        private dialogClientService: DialogClientService,
        public attachmentDialogBroker: RightshipEntityAttachmentManager,
        public attachmentDialogBrokerCfr: RightshipEntityAttachmentManager,
        public entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private cfrService: CfrService,
        lookupClientService: LookupClientService
    ) {
        super(notificationsService, i18N);
        this.currentUser = userClientService.currentUser;
        this.canEditFbrOutcome = this.userClaimsService.hasPermission(Claims.CanEditFbrOutcome);
        this.canReviewFbr = this.userClaimsService.hasPermission(Claims.CanReviewFBR);
        this.canAssignFbrRequest = this.userClaimsService.hasPermission(Claims.CanAssignFbrRequest);
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canAddCFRPermission = this.userClaimsService.hasPermission(Claims.CanAddCFR);
        this.incidentEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Incidents"]);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.CfrStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.cfrStatuses = x.activeValues;
        });

        this.currentUserId = this.userClientService.currentUser.id;
    }

    currentItem: FbrItemClientModel;
    comments: string;

    displayName(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.displayName;
    }

    isTextBox(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.Default ||
            column.tableColumnDataTypeId == DataType.Number ||
            column.tableColumnDataTypeId == DataType.Double;
    }

    getTextBoxType(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        if (column.tableColumnDataTypeId == DataType.Default) {
            return "text";
        }
        if (column.tableColumnDataTypeId == DataType.Number) {
            return "integer";
        }
        if (column.tableColumnDataTypeId == DataType.Double) {
            return "double";
        }
    }

    isCheckbox(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.Boolean;
    }

    isDate(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.DateTime;
    }

    async viewFbrItem(item: FbrItemClientModel) {
        this.currentItem = item;
        this.comments = "";

        this.attachmentDialogBrokerCfr.items = [];
        this.attachmentDialogBrokerCfr.rootItem = <any>{
            entityType: EntityTypes.FBRItem,
            id: this.currentItem.fbrItemId
        };
        await this.attachmentDialogBrokerCfr.loadAttachments();

        setTimeout(() => {
            if (this.currentItem && this.currentItem.element) {
                this.currentItem.element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }, 100);
    }

    openCfrStatusDialog(item: FbrItemClientModel) {
        const statusIsFinal = item && (item.cfrStatusId == CfrStatusEnum.Closed || item.cfrStatusId == CfrStatusEnum.Cancelled)
        if (statusIsFinal) return;


        if (this.userClaimsService.hasPermission(Claims.CanAccessCFRs)) {
            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Cfr, { id: item.cfrId });
        }

        // if (this.canEditFbrOutcome) {
        //     if (this.item.assignedUserId == this.userClientService.currentUser.id) {

        //         let reloadItemFunc = () => {
        //             this.fbrService.getFbrItems(this.item.fbrId).then(x => {
        //                 this.item.fbrItems = x;
        //                 this.viewFbrItem(this.item.fbrItems.filter(a => a.fbrItemId == item.fbrItemId)[0]);
        //             });
        //         }

        //         this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrStatusDialog,
        //             {
        //                 formName: item.title,
        //                 otherComments: this.item.comments,
        //                 cfrId: item.cfrId,
        //                 cfrStatusId: item.cfrStatusId,
        //                 onDraftGenerated: reloadItemFunc,
        //                 onSubmit: reloadItemFunc,
        //                 vesselName: this.item.vesselName,
        //                 internationalMaritimeOrganizationNumber: this.item.internationalMaritimeOrganizationNumber,
        //                 europeanNumberOfIdentification: this.item.europeanNumberOfIdentification,
        //                 vehicleIdentificationNumber: this.item.vehicleIdentificationNumber
        //             }, true, false, false, 'right');
        //     } else {
        //         this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
        //             name: "Condition for Review",
        //             submit: this.assignToMe.bind(this),
        //             callback: this.openCfrStatusDialog.bind(this),
        //             currentAssignment: this.assignment.assignedEntity.assignedTo
        //         }, true, false, false, "right");
        //     }
        // }
    }

    saveComments() {
        return new Promise<void>((resolve, reject) => {
            this.fbrService.saveComments(this.item.fbrId, [{
                fbrItemId: this.currentItem.fbrItemId,
                comment: this.currentItem.comment,
                flagForAllVets: this.currentItem.flagForAllVets,
                flagForCustomer: this.currentItem.flagForCustomer
            }], this.item.comments).then(() => {
                this.currentItem.commentDateModified = new Date();
                this.notificationClientService.show("FBR Item Comment", "FBR Item Comment saved successfully", NotificationType.Success);
                resolve();
            }, () => {
                this.notificationClientService.show("FBR Item Comment", "FBR Item Comment failed to save", NotificationType.Error);
                reject();
            });
        });
    }

    async activate(model: { item: FbrClientModel, assignment: Assignment }) {
        super.activate(model.item);
        this.assignment = model.assignment;
        this.currentItem = this.fbrItems[this.firstNonEmptyArray(this.fbrItems)][0];
        this.attachmentDialogBrokerCfr.items = [];
        this.attachmentDialogBrokerCfr.rootItem = <any>{
            entityType: EntityTypes.FBRItem,
            id: this.currentItem.fbrItemId
        };
        await this.attachmentDialogBrokerCfr.loadAttachments();
    }

    protected onActivated(model: FbrClientModel) {
        super.onActivated(model);

        if (this.item) {
            this.loadRFIs();
        }
    }

    @computedFrom(Reflection.getPropertyPath<FbrResponses>(v => v.item), Reflection.getPropertyPath<FbrResponses>(v => v.item.fbrStatusId))
    public get statusIsFinal(): boolean {
        return this.item && (this.item.fbrStatusId == FbrStatusEnum.Closed || this.item.fbrStatusId == FbrStatusEnum.Expired);
    }

    closingRFI: boolean;
    
    @computedFrom(Reflection.getPropertyPath<FbrResponses>(v => v.item),
     Reflection.getPropertyPath<FbrResponses>(v => v.item.fbrItems),
     Reflection.getPropertyPath<FbrResponses>(v => v.item.fbrItems.length))
    get fbrItems(): { [index: number]: FbrItemClientModel[] } {
        const tasks = {};
        const cfrStatusRank = {
            1: 1,
            2: 2,
            3: 3,
            4: 4,
            5: 6,
            6: 5
        }

        const fbrItems = this.item.fbrItems;

        tasks[FbrItemGroupingEnum.UnsatisfactoryRequiresReview] = Enumerable.from(fbrItems).where(x => x.rightShipReviewRequired && x.outcome == FbrItemOutcomeStepEnum.Fail)
            .orderBy(x => cfrStatusRank[x.cfrStatusId]).toArray();
        tasks[FbrItemGroupingEnum.UnsatisfactoryNoReviewRequired] = Enumerable.from(fbrItems).where(x => !x.rightShipReviewRequired && x.outcome == FbrItemOutcomeStepEnum.Fail)
            .orderBy(x => x.sequenceNumber).toArray();
        tasks[FbrItemGroupingEnum.Satisfactory] = Enumerable.from(fbrItems).where(x => x.outcome == FbrItemOutcomeStepEnum.Pass)
            .orderBy(x => x.sequenceNumber).toArray();
        tasks[FbrItemGroupingEnum.NotObserved] = Enumerable.from(fbrItems).where(x => x.outcome == FbrItemOutcomeStepEnum.NA)
            .orderBy(x => x.sequenceNumber).toArray();

        return tasks;
    } 

    public fbrItemKeys = [
            FbrItemGroupingEnum.UnsatisfactoryRequiresReview,
            FbrItemGroupingEnum.UnsatisfactoryNoReviewRequired,
            FbrItemGroupingEnum.Satisfactory,
            FbrItemGroupingEnum.NotObserved
        ];
    

    getTitle(taskGroup: string) {
        const x = Number(taskGroup) as FbrItemGroupingEnum;
        switch (x) {
            case FbrItemGroupingEnum.NotObserved:
                return "Not Observed";
            case FbrItemGroupingEnum.Satisfactory:
                return "Satisfactory";
            case FbrItemGroupingEnum.UnsatisfactoryNoReviewRequired:
                return "Unsatisfactory, however no investigation required";
            case FbrItemGroupingEnum.UnsatisfactoryRequiresReview:
                return "Unsatisfactory, requires RightShip investigation";
        }
    }

    closeRuleDetails() {
        this.currentItem = null;
        this.comments = "";
    }


    attached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Cfr), this.refreshCfrStatus.bind(this));
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Cfr), this.refreshCfrStatus.bind(this));
    }

    refreshCfrStatus(param: EntityStatusChangeModel) {

        if (!this.currentItem) return;

        if (this.currentItem.cfrStatusId == CfrStatusEnum.Closed || this.currentItem.cfrStatusId == CfrStatusEnum.Cancelled) return;
        if (this.currentItem.cfrId == param.entityId) {

            if (this.assignment && param.statusId != CfrStatusEnum.CfrReassignment) {
                this.assignment.reload().then(x => this.item.assignedUserId = x.assignedTo);
            }

            const vetStatus = this.cfrStatuses.filter(x => x.id == param.statusId)[0];

            if (vetStatus) {
                this.currentItem.cfrStatus = vetStatus.name;
                this.currentItem.cfrStatusId = vetStatus.id;
            }
        }
    }
    
    firstNonEmptyArray(object) {
        for (const property in object) {
            if (object[property].length > 0) {
                return property;
            }
        }
    }

    toggle(element: HTMLElement, arrow: HTMLElement) {
        $(element).slideToggle()
        if ($(arrow).hasClass('is-opened')) {
            $(arrow).removeClass('is-opened');
        } else {
            $(arrow).addClass('is-opened');
        }
    }

    addSubtask() {
        this.addRFIs();
    }


    countRFIs: number = 0;
    rfis: { [index: number]: AddSubtaskRequest } = {};

    addRFIs() {
        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddSubtaskDialog, {
                entityId: this.item.fbrId,
                entityTypeId: this.item.entityType,
                isWet: this.item.cargoTypeCategoryId == CargoTypeCategoryEnum.Wet,
                requestedBy: this.item.requestedBy,
                createSubtask: true,
                isPartialRFI: true,
                reload: (() => {
                    this.loadRFIs();
                    this.fbrService.getFbrItems(this.item.fbrId).then(x => {
                        this.item.fbrItems = x;
                    });
                }).bind(this)
            },
                true, false, false, 'right');
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.FbrAssignmentDialog, {
                name: "FBR",
                submit: this.assignToMe.bind(this),
                callback: this.addRFIs.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }

    }

    rfi(item: FbrItemClientModel) {
        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddSubtaskDialog, {
                isWet: this.item.cargoTypeCategoryId == CargoTypeCategoryEnum.Wet,
                entityId: this.item.fbrId,
                entityTypeId: this.item.entityType,
                linkedEntityId: item.fbrItemId,
                linkedEntityTypeId:  EntityTypes.FBRItem,
                isPartialRFI: true,
                subtaskTypes: item.subtaskTypes,
                reload: (() => {
                    this.loadRFIs();
                }).bind(this)
            },
                true, false, false, 'right');
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.FbrAssignmentDialog, {
                name: "FBR",
                submit: this.assignToMe.bind(this),
                callback: this.rfi.bind(this, item),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    private loadRFIs() {
        this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(`RFI-FBR-${this.item.fbrId}`, CacheLocation.SessionStorage, false).then(x => {
            if (x) {
                this.countRFIs = Enumerable.from(x).where(x => x.value != null).count();
                this.rfis = x;
            } else {
                this.countRFIs = 0;
                this.rfis = {};
            }
        });
    }

    // LINKED INCIDENTS
    incidentEntityTypes: EntityTypeConfigurationModel[];
    incidentLinkingVisible: boolean = false;
    incidentIds: number[] = [];

    newIncident() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddIncident, {
            cfrId: this.currentItem.cfrId,
            fbrId: this.item.fbrId,
            id: 0,
            vesselId: this.item.vesselId,
            internationalMaritimeOrganizationNumber:
                this.item.internationalMaritimeOrganizationNumber ? this.item.internationalMaritimeOrganizationNumber : '0',
            vesselName: this.item.vesselName
        })
    }

    async linkToIncident() {
        try {
            const promise: Promise<LinkedIncidentModel[]> = this.cfrService.linkToIncident(this.currentItem.cfrId, this.incidentIds);
            const inc = await promise;

            this.incidentLinkingVisible = false;
            for (const i of inc)
                this.currentItem.linkedIncidents.push(i);
            this.incidentIds = [];
        } catch (e) {
            this.notificationClientService.show("Failed to link incident", "", NotificationType.Error);
        }
    }

    async unlinkIncident(incidentId: number) {
        try {
            const promise: Promise<void> = this.cfrService.unlinkIncident(this.currentItem.cfrId, incidentId);
            await promise;

            const incs = this.currentItem.linkedIncidents.filter(x => x.incidentId == incidentId);

            for (const i of incs) {
                const index = this.currentItem.linkedIncidents.indexOf(i);
                if (index >= 0)
                    this.currentItem.linkedIncidents.splice(index, 1);
            }

        } catch (e) {
            this.notificationClientService.show("Failed to un-link incident", "", NotificationType.Error);
        }
    }

    showIncidentLinking() {
        this.incidentLinkingVisible = true;
    }

    cancelIncidentLinking() {
        this.incidentLinkingVisible = false;
    }

    @computedFrom("item.fbrItems.length", "fbrResponsesContent", "currentItem")
    get paddingTop() {

        if(!MobileHelper.isMobileDevice() && window.innerWidth > 1024){
            if (Enumerable.from(this.fbrItems).sum(x => x.value.length) > 4) {
                const task = Enumerable.from(this.fbrItems).selectMany<FbrItemClientModel>(x => x.value)
                    .firstOrDefault(x => this.currentItem && this.currentItem.fbrItemId == x.fbrItemId);
                if (task && task.element && this.fbrResponsesContent)
                    return task.element.offsetTop - this.fbrResponsesContent.offsetTop;
            }
        }

        return 0;
    }
    
    newCfr() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.NewCfr, {
            vesselId: this.item.vesselId,
            fbrItemId: this.currentItem.fbrItemId,
            businessUnitId: this.item.requestedOnBehalfOfBusinessUnitId ?? this.item.requestedByBusinessUnitId,
            customerId: this.item.requestedOnBehalfOfCustomerId ?? this.item.requestedByCustomerId,
            description: this.currentItem.description,
            title: this.currentItem.question,
            attachmentsToCopy: this.attachmentDialogBrokerCfr.items.map(x => { return { entityAttachmentId: x.entityAttachmentId, name: x.name }})
        });
    }
}